import React from 'react';
import {Spinner} from '@genestack/ui';
import style from './neighborhood-graph.module.css';
import {useRequestNeighborhoodGraph} from './hooks/use-request-neighborhood-graph';
import {NeighborhoodGraphLoaded} from './neighborhood-graph-loaded';
import {NeighborhoodGraphHeader} from './neighborhood-graph-header';
import {GraphDisplayErr} from '../../../../../components/graph/components/graph-display-err';
import {useNeighborhoodGraphSettings} from './hooks/use-neighborhood-graph-settings';
import {useGraphSelectionProps} from '../../../../../components/graph/graph-logic/use-graph-selection-props';
import {useDocLayoutSettings} from '../../hooks/use-doc-layout-settings';
import {useOnNodesLoaded} from '../../../../../components/graph/graph-logic/use-on-nodes-loaded';
import {GraphNode} from '../../../../../components/graph/interface';

interface Props {
    documentId: string;
    globalVersionId: number;

    selectedFacts: string[];
    closeGraph: () => void;
    graphSelectionProps: ReturnType<typeof useGraphSelectionProps>;
    layoutSettings: ReturnType<typeof useDocLayoutSettings>;
    graphSettings: ReturnType<typeof useNeighborhoodGraphSettings>;
}

export function NeighborhoodGraph(props: Props) {
    const {
        graphSettings: {
            maxDistance,
            viewKind,
            toggleSelectedViewKind,
            setMaxDistance,
            direction,
            setDirection
        },
        graphSelectionProps: {onNodesLoaded}
    } = props;

    const neighborhoodGraphRequest = useRequestNeighborhoodGraph({
        documentId: props.documentId,
        factIds: props.selectedFacts,
        globalVersionId: props.globalVersionId,
        maxDistance,
        direction,
        viewKind
    });

    const {canFetchNodeStructure} = useOnNodesLoaded({
        queryGraphRequest: neighborhoodGraphRequest,
        onLoaded: (nodes: GraphNode[]) => {
            onNodesLoaded(nodes);
        }
    });

    return (
        <div className={style.neighborhoodGraph}>
            <NeighborhoodGraphHeader
                closeGraph={props.closeGraph}
                isCollapsedPanel={props.layoutSettings.isGraphPanelCollapsed}
                toggleIsCollapsedPanel={props.layoutSettings.toggleIsGraphPanelCollapsed}
                viewKind={viewKind}
                toggleSelectedViewKind={toggleSelectedViewKind}
                maxDistance={maxDistance}
                setMaxDistance={setMaxDistance}
                direction={direction}
                setDirection={setDirection}
            />
            {(function () {
                if (!neighborhoodGraphRequest.data) {
                    return (
                        <div className={style.neighborhoodGraphLoader}>
                            <Spinner size={48} />
                        </div>
                    );
                }

                if (!neighborhoodGraphRequest.data.edges.length) {
                    return (
                        <GraphDisplayErr label="The neighborhood graph is not displayed because it has no edges." />
                    );
                }

                return (
                    <NeighborhoodGraphLoaded
                        nodes={neighborhoodGraphRequest.data.nodes}
                        edges={neighborhoodGraphRequest.data.edges}
                        selectedFacts={props.selectedFacts}
                        graphSettings={props.graphSettings}
                        documentId={props.documentId}
                        globalVersionId={props.globalVersionId}
                        graphSelectionProps={props.graphSelectionProps}
                        layoutSettings={props.layoutSettings}
                        canFetchNodesStructure={canFetchNodeStructure}
                    />
                );
            })()}
        </div>
    );
}
